import { createContext, useCallback, useState } from 'react';

/** How long a value changed by a refresh stays lit. */
export const REFRESH_FLASH_MS = 1000;

/**
 * Which periodic refresh the data on screen came from. A page bumps it in the
 * same update that puts a refresh's answer on screen (see useRefreshGeneration),
 * which is how a `RefreshFlash` (RefreshFlash.tsx) tells a value somebody else changed — it moved
 * together with the generation — from one this viewer just changed with a tap,
 * which moves on its own and is not lit: they already watched it happen.
 *
 * 0 outside any page that refreshes, where nothing ever lights up.
 */
export const RefreshGenerationContext = createContext(0);

/** The generation to provide, and the bump to call with each refresh's answer. */
export function useRefreshGeneration() {
  const [generation, setGeneration] = useState(0);
  const bump = useCallback(() => setGeneration((current) => current + 1), []);
  return [generation, bump] as const;
}

/** A boulder card's signature: its marks in order, and what they add up to. */
export const boulderCardSignature = (
  attempts: ReadonlyArray<{ id?: string | null; attempt_type: string }>,
  topped: boolean,
  zone: boolean
) => `${attempts.map((attempt) => `${attempt.id ?? ''}:${attempt.attempt_type}`).join(',')}|${topped}|${zone}`;

/** A lead card's signature: its two numbers and the style. */
export const leadCardSignature = (
  card: { holds_count?: string | number | null; time_seconds?: number | null; climbed_as?: string | null } | null | undefined
) => (card ? `${card.holds_count ?? ''}|${card.time_seconds ?? ''}|${card.climbed_as ?? ''}` : '');
